import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BRAND_ASSETS, PHOTOGRAPHY } from "../lib/constants";
import Overline from "../components/Overline";
import Reveal from "../components/Reveal";
import PatternStrip from "../components/PatternStrip";

const About = () => {
  return (
    <div data-testid="page-about">
      {/* Cinematic header */}
      <section className="relative h-[70vh] md:h-[80vh] overflow-hidden">
        <img
          src={PHOTOGRAPHY.landscape}
          alt="Sidama highlands"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2C3812]/40 via-[#2C3812]/30 to-[#F6EBE2]" />
        <div className="relative z-10 h-full flex flex-col justify-end max-w-7xl mx-auto px-6 md:px-12 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Overline color="cream">Our Story</Overline>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.15 }}
            className="font-serif-display text-[56px] md:text-[120px] leading-[0.92] text-elle-cream mt-6 max-w-5xl"
          >
            What we have <span className="italic">seen.</span>
          </motion.h1>
        </div>
      </section>

      {/* Meaning of Ell'e */}
      <section className="py-28 md:py-36">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>The Meaning</Overline>
            <h2 className="font-serif-display text-5xl md:text-6xl text-elle-green mt-6 leading-[1.05]">
              Ell'e (<span className="italic">ell-eh</span>) — <br className="hidden md:block" />
              a word for memory.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="drop-cap mt-12 text-[17px] md:text-[19px] leading-[1.85] text-elle-soft max-w-3xl">
              Long before coffee became a global ritual, before cafés and
              morning routines, it grew wild in the highlands of Ethiopia — in
              places like Sidamo, where the land rises into mist-covered
              mountains and fertile red soil nourishes generations. In the
              Sidama language, <em>Ell'e</em> means <em>what I have seen</em>.
              It is a word for memory, for witnessing, for heritage passed in
              quiet hands. Our coffee carries this word, and the weight of it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Sidama connection */}
      <section className="relative bg-elle-muted py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14 items-center">
          <Reveal className="md:col-span-6">
            <Overline>Sidama · Ethiopia</Overline>
            <h2 className="font-serif-display text-5xl md:text-6xl text-elle-green mt-6 leading-[1.05]">
              The place coffee first <span className="italic">grew.</span>
            </h2>
            <p className="mt-8 text-[16px] leading-relaxed text-elle-soft max-w-xl">
              In Sidama, coffee is not just a crop. It is part of life itself.
              It is grown by hand. Picked with care. Shared in ceremony. It is
              where people gather, where stories are told, where time slows
              down. Our beans come from this same soil — from smallholder
              farms at 1,800 – 2,200 metres, where altitude concentrates flavor
              and slow ripening gives the coffee its voice.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <img src={PHOTOGRAPHY.cherries} alt="Coffee cherries" className="aspect-[3/4] object-cover w-full" />
              <img src={PHOTOGRAPHY.beans} alt="Roasted beans" className="aspect-[3/4] object-cover w-full mt-8" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Maya — family inspiration */}
      <section className="py-28 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14 items-center">
          <Reveal className="md:col-span-5 order-2 md:order-1">
            <div className="relative aspect-[4/5] overflow-hidden bg-elle-sand">
              <img
                src={BRAND_ASSETS.bagBack}
                alt="Ell'e Coffee story"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7 order-1 md:order-2">
            <Overline>For Maya</Overline>
            <h2 className="font-serif-display text-5xl md:text-6xl text-elle-green mt-6 leading-[1.05]">
              A coffee named for the <span className="italic">hands that raised us.</span>
            </h2>
            <p className="mt-8 text-[16px] leading-relaxed text-elle-soft">
              Ell'e was born from family — from Maya, from mornings of
              grinding and boiling and pouring, from the patience of women who
              taught us to taste. This brand is an offering back to her and to
              every hand, every grandmother, every farmer that shaped our palate
              and our sense of place.
            </p>
            <blockquote className="mt-10 border-l border-elle-red pl-6 py-2">
              <p className="font-serif-display italic text-3xl md:text-4xl text-elle-green leading-tight">
                "Coffee is not a drink. It is a ceremony of being seen."
              </p>
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* Values grid */}
      <section className="py-28 bg-elle-green text-elle-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline color="cream">Our Principles</Overline>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-6 max-w-3xl leading-[1.05]">
              Slow, single-origin, honest.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-10 md:gap-14 mt-20">
            {[
              { t: "Single Origin", d: "No blends. Every bag traces back to one region, one altitude, one ceremony." },
              { t: "Hand Picked", d: "Cherries are picked cherry-by-cherry at peak ripeness — slower, but honest to the fruit." },
              { t: "Small Batch", d: "Roasted in intimate batches so each expression keeps its aromatic fingerprint." },
            ].map((v, i) => (
              <Reveal key={v.t} delay={i * 0.1}>
                <div className="border-t border-elle-cream/25 pt-8">
                  <h3 className="font-serif-display text-3xl">{v.t}</h3>
                  <p className="mt-4 text-elle-cream/80 leading-relaxed text-[15px]">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-20">
            <Link
              to="/shop"
              data-testid="about-shop-link"
              className="inline-flex items-center gap-3 bg-elle-cream text-elle-green px-8 py-4 text-[11px] tracking-overline uppercase hover:bg-white transition-colors duration-500"
            >
              Shop the coffees
            </Link>
          </div>
        </div>
        <PatternStrip height={10} className="mt-24" />
      </section>
    </div>
  );
};

export default About;
